import { Box, CircularProgress, Alert } from '@mui/material'
import DataTable from 'Components/Table/DataTable'
import { Column, type RowData } from 'Components/Table/DataTable/DataTable.model'
import { useEffect, useState } from 'react'
import { type globalSettingValue } from 'core/types'
import { sendGet, sendPut, sendPost } from 'hooks/use-fetch'
import { usePagination, type PaginationProps } from 'hooks/use-pagination'
import { NameMatchSettingDetails } from 'Pages/ApplicationSettings/NameMatchSettingDetails'
import { toast } from 'react-toastify'

const nameColumn = new Column('name', 'Name')
const valueColumn = new Column('value', 'Value')
const columns = [nameColumn, valueColumn]

export function NameMatchSettingList (): JSX.Element {
  const [refreshTime, setRefreshTime] = useState(new Date())
  const [selectedSetting, setSelectedSetting] = useState<globalSettingValue | null>()
  const [open, setOpen] = useState(false)
  const [settingList, setSettingList] = useState<globalSettingValue[]>([])
  const [warnings, setWarnings] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const paginationProps: PaginationProps<RowData> = {
    rows: settingList
  }
  const pagination = usePagination(paginationProps)

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      const { response: fplResponse } = await sendGet('/GlobalSetting/GetList', {})
      if (fplResponse !== null) {
        setSettingList(fplResponse)
      }
      setLoading(false)

      const { response: rsp } = await sendPost('/GlobalSetting/NameMatchSettingWarning', {})
      setWarnings(rsp as string | null)
    }
    void loadData()
  }, [refreshTime])

  const rowClick = (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, row: RowData): void => {
    const selectedYear: globalSettingValue = {
      name: row.name,
      type: row.type,
      value: row.value
    }
    setSelectedSetting(selectedYear)
    setOpen(true)
  }

  const handleSubmit = async (editData: globalSettingValue): Promise<void> => {
    const { response: rsp } = await sendPut('/GlobalSetting/Update', editData)
    const result: { newRecordID: number, errors: string[], success: boolean } = rsp
    if (result.success) {
      setOpen(false)
      setRefreshTime(new Date())
    } else {
      result.errors?.forEach((e) => {
        toast.error(e)
      })
    }
  }

  if (loading) return <CircularProgress />
  return <Box>
    {warnings != null && <Alert severity="warning" sx={{ border: 1, borderColor: 'orange' }}>{warnings}</Alert> }
    <DataTable
      page={pagination.page}
      data-testid="table"
      name='settingTable'
      columns={columns}
      hasCheckbox={false}
      rows={pagination.internalRows ?? []}
      totalRecords={pagination.recordCount}
      loading={loading}
      onSortChange={(col, sorts) => {
        pagination.onSortChange(col, sorts[0]?.order)
      }}
      onPageChange={pagination.handleChangePage}
      onRowsPerPageChange={pagination.handleChangeRowsPerPage}
      onRowClick={rowClick}
    />
    {open && selectedSetting != null && (
      <NameMatchSettingDetails
        onClose={() => { setOpen(false) }}
        onSubmit={handleSubmit}
        open={open}
        setting={selectedSetting}
      />
    )
    }
  </Box>
}
